import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { FiscalYearProvider } from './contexts/FiscalYearContext';
import { TourProvider } from './contexts/TourContext';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationDialog from './components/NotificationDialog';
import './services/logService'; // Initialize logging
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectForm = lazy(() => import('./pages/ProjectForm'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const WBSManagement = lazy(() => import('./pages/WBSManagement'));
const Documents = lazy(() => import('./pages/Documents'));
const ImageDocumentation = lazy(() => import('./pages/ImageDocumentation'));
const VideoDocumentation = lazy(() => import('./pages/VideoDocumentation'));
const Comments = lazy(() => import('./pages/Comments'));
const Alerts = lazy(() => import('./pages/Alerts'));
const ProjectMembers = lazy(() => import('./pages/ProjectMembers'));
const Profile = lazy(() => import('./pages/Profile'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportViewer = lazy(() => import('./pages/ReportViewer'));
const Search = lazy(() => import('./pages/Search'));
const Users = lazy(() => import('./pages/Users'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const Persons = lazy(() => import('./pages/Finance/Persons'));
const BankAccounts = lazy(() => import('./pages/Finance/BankAccounts'));
const BankGroups = lazy(() => import('./pages/Finance/BankGroups'));
const BankTypes = lazy(() => import('./pages/Finance/BankTypes'));
const Transactions = lazy(() => import('./pages/Finance/Transactions'));
const Checks = lazy(() => import('./pages/Finance/Checks'));
const CostCenters = lazy(() => import('./pages/Finance/CostCenters'));
const FieldsOfStudy = lazy(() => import('./pages/Finance/FieldsOfStudy'));
const MilitaryStatuses = lazy(() => import('./pages/Finance/MilitaryStatuses'));
const ExpenseGroups = lazy(() => import('./pages/Expenses/ExpenseGroups'));
const ExpenseTypes = lazy(() => import('./pages/Expenses/ExpenseTypes'));
const Expenses = lazy(() => import('./pages/Expenses/Expenses'));
const Departments = lazy(() => import('./pages/Expenses/Departments'));
const Contracts = lazy(() => import('./pages/Contractors/Contracts'));
const ContractHub = lazy(() => import('./pages/Contractors/ContractHub'));
const ContractProgresses = lazy(() => import('./pages/Contractors/ContractProgresses'));
const ContractDashboards = lazy(() => import('./pages/Contractors/ContractDashboards'));
const ContractTypes = lazy(() => import('./pages/Contractors/ContractTypes'));
const DeductionTypes = lazy(() => import('./pages/Contractors/DeductionTypes'));
const Workplaces = lazy(() => import('./pages/Payroll/Workplaces'));
const Employers = lazy(() => import('./pages/Payroll/Employers'));
const Employees = lazy(() => import('./pages/Payroll/Employees'));
const EmployeeDetails = lazy(() => import('./pages/Payroll/EmployeeDetails'));
const PayrollAttendance = lazy(() => import('./pages/Payroll/Attendance'));
const PayrollProcessing = lazy(() => import('./pages/Payroll/PayrollProcessing'));
const PayrollReportsHub = lazy(() => import('./pages/Payroll/PayrollReportsHub'));
const Loans = lazy(() => import('./pages/Payroll/Loans'));
const PayrollConfig = lazy(() => import('./pages/Payroll/PayrollConfig'));
const ContractReport = lazy(() => import('./pages/Payroll/ContractReport'));
const PayrollList = lazy(() => import('./pages/Payroll/PayrollList'));
const ContractReportNew = lazy(() => import('./pages/Payroll/ContractReportNew'));
const Currencies = lazy(() => import('./pages/Payroll/Currencies'));
const MenuManagement = lazy(() => import('./pages/Settings/MenuManagement'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const SmtpSettings = lazy(() => import('./pages/Settings/SmtpSettings'));
const TourManagement = lazy(() => import('./pages/TourManagement'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const FileSharing = lazy(() => import('./pages/FileSharing'));
const ChartOfAccounts = lazy(() => import('./pages/Accounting/ChartOfAccounts'));
const FiscalYears = lazy(() => import('./pages/Accounting/FiscalYears'));
const Vouchers = lazy(() => import('./pages/Accounting/Vouchers'));
const TrialBalance = lazy(() => import('./pages/Accounting/TrialBalance'));
const LegalBooks = lazy(() => import('./pages/Accounting/LegalBooks'));
const FinancialReports = lazy(() => import('./pages/Accounting/FinancialReports'));
const DetailCodes = lazy(() => import('./pages/Accounting/DetailCodes'));
const FloatingCenters = lazy(() => import('./pages/Accounting/FloatingCenters'));
const AccountingMappings = lazy(() => import('./pages/Accounting/AccountingMappings'));
const CompareLists = lazy(() => import('./pages/PayrollCompare/CompareLists'));
const CompareEmployees = lazy(() => import('./pages/PayrollCompare/CompareEmployees'));
const InventoryItems = lazy(() => import('./pages/Inventory/Items'));
const InventoryWarehouses = lazy(() => import('./pages/Inventory/Warehouses'));
const InventoryDocuments = lazy(() => import('./pages/Inventory/Documents'));
const SalesInvoices = lazy(() => import('./pages/Sales/Invoices'));
const SalesBaseData = lazy(() => import('./pages/Sales/BaseData'));
const ProductionBoms = lazy(() => import('./pages/Production/Boms'));
const ProductionBatches = lazy(() => import('./pages/Production/Batches'));
const ReportCardex = lazy(() => import('./pages/Reports/Cardex'));
const ReportStockBalance = lazy(() => import('./pages/Reports/StockBalance'));
const ReportSales = lazy(() => import('./pages/Reports/SalesReport'));
const StockOrders = lazy(() => import('./pages/Inventory/StockOrders'));
const CountDocs = lazy(() => import('./pages/Inventory/CountDocs'));
const SalesOrders = lazy(() => import('./pages/Sales/Orders'));
const ProdSerials = lazy(() => import('./pages/Production/Serials'));
const PostingPage = lazy(() => import('./pages/Posting/Posting'));
const ModuleSettings = lazy(() => import('./pages/Settings/ModuleSettings'));
const ReportVisitorCommission = lazy(() => import('./pages/Reports/VisitorCommission'));
const InventoryBaseData = lazy(() => import('./pages/Inventory/BaseData'));
const ReportProduction = lazy(() => import('./pages/Reports/ProductionReport'));

const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: 'Vazirmatn, sans-serif',
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          textAlign: 'right',
        },
        head: {
          textAlign: 'right',
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          textAlign: 'right',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
      <AuthProvider>
        <FiscalYearProvider>
        <BrowserRouter>
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', direction: 'rtl' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={44} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>در حال بارگذاری...</Typography>
            </Box>
          </Box>
        }>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* صفحات مقایسه حقوق — عمومی، بدون نیاز به لاگین */}
          <Route path="/payroll-compare/lists" element={<CompareLists />} />
          <Route path="/payroll-compare/employees" element={<CompareEmployees />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Projects />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/wbs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WBSManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Documents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/documents/wbs/:wbsItemId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Documents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/images"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ImageDocumentation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/images/wbs/:wbsItemId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ImageDocumentation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/videos"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VideoDocumentation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/videos/wbs/:wbsItemId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VideoDocumentation />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/comments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Comments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/comments/wbs/:wbsItemId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Comments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/members"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectMembers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Alerts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:projectId?"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Search />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyTasks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Layout>
                      <Users />
                    </Layout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            
            {/* Finance Routes */}
            <Route
              path="/finance/persons"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Persons />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-accounts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BankAccounts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-groups"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BankGroups />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-types"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BankTypes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/transactions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Transactions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/checks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Checks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/cost-centers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CostCenters />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/fields-of-study"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FieldsOfStudy />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/military-statuses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MilitaryStatuses />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Expense Routes */}
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/types"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseTypes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/groups"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseGroups />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/departments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Departments />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Contractor Routes */}
            <Route
              path="/contractors/contract-dashboards"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractDashboards />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Contracts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract-progresses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractProgresses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contractors/contract-types"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractTypes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contractors/deduction-types"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeductionTypes />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Payroll Routes */}
            <Route
              path="/payroll/employees"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/employees/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/workplaces"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Workplaces />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/employers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/payroll-list"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/attendance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollAttendance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/processing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollProcessing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/reports-hub"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollReportsHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/loans"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Loans />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/config"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollConfig />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/currencies"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Currencies />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/reports/contracts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractReport />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/payroll-decrees"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContractReportNew />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/view/:reportCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReportViewer />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/menu"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MenuManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/company"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CompanySettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/smtp"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SmtpSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tour-management"
              element={
                <AdminRoute>
                  <Layout>
                    <TourManagement />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <AdminRoute>
                  <Layout>
                    <RoleManagement />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/file-sharing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FileSharing />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Accounting Routes */}
            <Route
              path="/accounting/chart-of-accounts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChartOfAccounts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/fiscal-years"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FiscalYears />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/vouchers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Vouchers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/trial-balance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrialBalance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/legal-books"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LegalBooks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/financial-reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FinancialReports />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/accounting/detail-codes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DetailCodes />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/accounting/floating-centers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FloatingCenters />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/accounting/mappings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountingMappings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/inventory/items" element={<ProtectedRoute><Layout><InventoryItems /></Layout></ProtectedRoute>} />
            <Route path="/inventory/warehouses" element={<ProtectedRoute><Layout><InventoryWarehouses /></Layout></ProtectedRoute>} />
            <Route path="/inventory/documents" element={<ProtectedRoute><Layout><InventoryDocuments /></Layout></ProtectedRoute>} />
            <Route path="/inventory/base-data" element={<ProtectedRoute><Layout><InventoryBaseData /></Layout></ProtectedRoute>} />
            <Route path="/sales/invoices" element={<ProtectedRoute><Layout><SalesInvoices /></Layout></ProtectedRoute>} />
            <Route path="/sales/base-data" element={<ProtectedRoute><Layout><SalesBaseData /></Layout></ProtectedRoute>} />
            <Route path="/production/boms" element={<ProtectedRoute><Layout><ProductionBoms /></Layout></ProtectedRoute>} />
            <Route path="/production/batches" element={<ProtectedRoute><Layout><ProductionBatches /></Layout></ProtectedRoute>} />
            <Route path="/reports/cardex" element={<ProtectedRoute><Layout><ReportCardex /></Layout></ProtectedRoute>} />
            <Route path="/reports/stock-balance" element={<ProtectedRoute><Layout><ReportStockBalance /></Layout></ProtectedRoute>} />
            <Route path="/reports/sales" element={<ProtectedRoute><Layout><ReportSales /></Layout></ProtectedRoute>} />
            <Route path="/inventory/orders" element={<ProtectedRoute><Layout><StockOrders /></Layout></ProtectedRoute>} />
            <Route path="/inventory/count-docs" element={<ProtectedRoute><Layout><CountDocs /></Layout></ProtectedRoute>} />
            <Route path="/sales/orders" element={<ProtectedRoute><Layout><SalesOrders /></Layout></ProtectedRoute>} />
            <Route path="/production/serials" element={<ProtectedRoute><Layout><ProdSerials /></Layout></ProtectedRoute>} />
            <Route path="/posting" element={<ProtectedRoute><Layout><PostingPage /></Layout></ProtectedRoute>} />
            <Route path="/settings/modules" element={<ProtectedRoute><Layout><ModuleSettings /></Layout></ProtectedRoute>} />
            <Route path="/reports/visitor-commission" element={<ProtectedRoute><Layout><ReportVisitorCommission /></Layout></ProtectedRoute>} />
            <Route path="/reports/production" element={<ProtectedRoute><Layout><ReportProduction /></Layout></ProtectedRoute>} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        </BrowserRouter>
        </FiscalYearProvider>
        <NotificationDialog />
      </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
